import {useEffect, useRef, useState} from 'react'
import {flightService} from '@/services/flightService'
import {isNearAirport} from '@/utils/airports'
import {Loader} from '@/customComponents/uiComponents/Loader.jsx'
import {SmartPickupRecommendation} from '@/customComponents/BookRideSteps/modalPages/smartPickupRecommendation.jsx'
import {SmartPickupResult} from '@/customComponents/BookRideSteps/modalPages/smartPickupResult.jsx'
import {LiabilityDisclaimerContent} from '@/customComponents/BookRideSteps/modalPages/liabilityDisclaimer.jsx'
import {FindingDriver} from '@/customComponents/BookRideSteps/modalPages/findingDriver.jsx'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'

/**
 * The booking flow's recommended-pickup flow, driven from the /edit and /confirm pages.
 *
 * Those pages have no booking in progress, so the two screens can't read the trip out
 * of session storage the way they do inside the booking modal. Everything they need is
 * passed in instead, and the outcome comes back through `onSelectTime` — the same
 * callback the old DateTimeSection used, so the pages' apply/close logic is unchanged.
 *
 * Screen order matches the booking flow: the chooser (type a time OR enter a flight
 * number), then the recommendation. `pick` holds the handoff between them in STATE
 * rather than the booking storage keys, so an edit can never leave a stale flight or
 * typed time behind for the next booking.
 *
 * @param {Array}    destinations  [pickup, dropoff, ...stopovers] — the layout the screens index
 * @param {string}   rideDate      seeds the calendar
 * @param {object}   routeData     `{duration_minutes}` for the drive-time row
 * @param {string}   flightNumber   prefills the flight-number field
 * @param {object}   flightDetails  a flight the page ALREADY has (edit/confirm normally
 *                                  do). Present ⇒ open straight on the recommendation
 *                                  for it, which is what the old DateTimeSection did by
 *                                  auto-looking-up a pre-filled number. Back returns to
 *                                  the chooser to change it.
 * @param {Function} onFlightChange ({flightNumber, flightDetails}) — a looked-up flight,
 *                                  so the page keeps the flight it already tracks (its
 *                                  submit gate reads it; the old DateTimeSection set it
 *                                  the same way)
 * @param {Function} onSelectTime   (Date) => void — the chosen pickup
 * @param {Function} onClose        dismiss the modal (also "Set pickup time manually")
 * @param {boolean}  hideBack       drop the recommendation's back arrow (confirm page:
 *                                  the booking's flight is already settled, so there is
 *                                  nothing to go back and re-pick)
 */
export default function SmartPickupFlow({
    destinations = [],
    rideDate,
    routeData,
    flightNumber,
    flightDetails,
    onFlightChange,
    onSelectTime,
    onClose,
    hideBack = false,
}) {
    // 'looking' → 'chooser' | 'result'. Local, so this never touches the booking step
    // machine. A trip that already carries a flight opens on the RECOMMENDATION —
    // re-asking for a number the trip already has would be a step backwards from the
    // screen this replaced. When only the NUMBER is known we look it up first (see the
    // effect below) and start on a loader, so the chooser never flashes on the way.
    const needsLookup = !!flightNumber && !flightDetails && !!rideDate
    const [screen, setScreen] = useState(
        flightDetails ? 'result' : needsLookup ? 'looking' : 'chooser',
    )
    const [pick, setPick] = useState(
        flightDetails ? {flightDetails, enteredTime: null, flightNumber} : null,
    )
    const lookedUp = useRef(false)

    /*
     * The caller's `onFlightChange` behind a ref.
     *
     * Every call site passes an INLINE arrow, so the prop is a new function on each of the
     * parent's renders. Naming it as an effect dependency is what made the lookup below
     * hang — see the note there.
     */
    const onFlightChangeRef = useRef(onFlightChange)
    useEffect(() => { onFlightChangeRef.current = onFlightChange })

    // Only the pickup's COORDINATES matter to the lookup, and `destinations` is rebuilt
    // (`[...].filter(Boolean)`) on every parent render — so depend on the two numbers,
    // never on the array identity.
    const pickupLat = destinations[0]?.lat
    const pickupLng = destinations[0]?.lng

    /*
     * Resolve a flight number the trip already carries into flight DETAILS.
     *
     * The screen this replaced (DateTimeSection) did this itself on mount; without it
     * nothing ever fetched, `flightDetails` stayed null, and a trip with a perfectly
     * good flight number still opened on the chooser asking for one.
     *
     * Direction decides which movement to match: a from-airport ride is met on ARRIVAL,
     * a to-airport ride departs. A failed lookup just falls back to the chooser, where
     * the number is pre-filled and the user can retry with Add.
     *
     * ⚠️ DEPS MUST STAY PRIMITIVE. They used to include `destinations` (a fresh array every
     * render) and `onFlightChange` (a fresh arrow every render), so the effect re-ran on
     * every parent render. The re-run early-returns on `lookedUp` — but React fires the
     * PREVIOUS run's cleanup first, which set `cancelled = true` on the request still in
     * flight. The answer was then thrown away and nothing ever retried, leaving the modal
     * on its loader for good. It only surfaced once the confirm pages started writing
     * changes through (each write invalidates the trip → refetch → re-render), which made a
     * re-render land inside the lookup window almost every time instead of occasionally.
     */
    useEffect(() => {
        if (lookedUp.current || !needsLookup) return
        lookedUp.current = true

        const fromAirport = !!(pickupLat && pickupLng && isNearAirport(pickupLat, pickupLng))
        const d = new Date(rideDate)
        const dateParam = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

        let cancelled = false
        ;(async () => {
            const res = await flightService.getFlightDetails(
                flightNumber, dateParam, fromAirport ? 'Arrival' : 'Departure',
            )
            if (cancelled) return
            const fd = res.success && res.data?.length ? res.data[0] : null
            if (fd) {
                setPick({flightDetails: fd, enteredTime: null, flightNumber})
                onFlightChangeRef.current?.({flightNumber, flightDetails: fd})
                setScreen('result')
            } else {
                setScreen('chooser')
            }
        })()
        return () => { cancelled = true }
    }, [needsLookup, flightNumber, rideDate, pickupLat, pickupLng])
    // The screens open these as overlay STEPS inside the booking modal; here there is no
    // step machine, so they render as their own sheets.
    const [liabilityOpen, setLiabilityOpen] = useState(false)
    const [findingDriverOpen, setFindingDriverOpen] = useState(false)

    // The screens call setStep for booking-only destinations. Two are internal moves
    // between the screens; `travelingDateTime` is the chooser's back arrow, which here
    // means "leave the flow". Anything else is already handled by a prop, so it falls
    // through and is ignored rather than throwing the page into an unknown step.
    const setStep = (next) => {
        if (next === 'smartPickupResult') setScreen('result')
        else if (next === 'smartPickupRecommendation') setScreen('chooser')
        else if (next === 'travelingDateTime') onClose?.()
    }

    const shared = {
        setStep,
        // The modal renders no header of its own, so the screens' own header carries the
        // only ✕ — without it the confirm page (no back arrow either) has no way out
        // but the backdrop.
        showClose: true,
        closeModal: onClose,
        destinations,
        rideDate,
        onManual: onClose,   // "Set pickup time manually" → back to the page's own picker
    }

    return (
        <>
            {screen === 'looking' ? (
                <div className="flex justify-center py-15"><Loader color="#201e20"/></div>
            ) : screen === 'chooser' ? (
                <SmartPickupRecommendation
                    {...shared}
                    flightNumber={flightNumber}
                    enteredTime={pick?.enteredTime}
                    flightType={pick?.flightType}
                    onPick={(next) => {
                        setPick(next)
                        // Hand a looked-up flight back to the page before moving on, so
                        // its flight field and submit gate see it.
                        if (next.flightDetails) {
                            onFlightChange?.({flightNumber: next.flightNumber, flightDetails: next.flightDetails})
                        }
                        setScreen('result')
                    }}
                />
            ) : (
                <SmartPickupResult
                    {...shared}
                    routeBreakdown={routeData}
                    flightDetails={pick?.flightDetails ?? null}
                    enteredTime={pick?.enteredTime ?? null}
                    flightType={pick?.flightType}
                    hideBack={hideBack}
                    onCommit={onSelectTime}
                    onOpenLiability={() => setLiabilityOpen(true)}
                    onOpenFindingDriver={() => setFindingDriverOpen(true)}
                />
            )}

            <Modal isOpen={liabilityOpen} onClose={() => setLiabilityOpen(false)}>
                <ModalHeader title="" onClose={() => setLiabilityOpen(false)}/>
                <div className="hopn-policies-disclaimer">
                    <LiabilityDisclaimerContent/>
                </div>
            </Modal>

            <Modal isOpen={findingDriverOpen} onClose={() => setFindingDriverOpen(false)}>
                <ModalHeader title="" onClose={() => setFindingDriverOpen(false)}/>
                <FindingDriver hideHeader/>
            </Modal>
        </>
    )
}
